import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {mapValues} from 'lodash';
import {NavigationService} from '../../../../../shared/navigation/index';
import {ProfileUserState} from '../../api/entity/index';
import {ProfileUser} from "../../../../../shared/api/entity/profile.users";
import {CustomerProfileUsersService} from "../../api/service/customer-profile-users.service";
import {ProfileUsersFilteringModel} from "../../api/entity/profile-users-filter-model";
import {NotificationManager} from "../../../../../../../common/providers/notification";
import {TranslateManager} from "../../../../../../../common/translate";
import {InjectOptionDialog, OptionDialog} from "../../../../../shared/option-dialog";
import {CountryService} from "../../../../../shared/components/dictionary/country/api/country.service";
import {Country} from "../../../../../shared/components/dictionary/country/api/country";
import {CUSTOMER_USERS_TAB_ROUTE} from "../../../../../core/security";

const STATE_CREATED_KEY = 'CUSTOMER.DETAILS.TAB.USERS.STATE.CREATED';
const STATE_APPROVED_KEY = 'CUSTOMER.DETAILS.TAB.USERS.STATE.APPROVED';
const STATE_DECLINED_KEY = 'CUSTOMER.DETAILS.TAB.USERS.STATE.DECLINED';
const STATE_EDITED_KEY = 'CUSTOMER.DETAILS.TAB.USERS.STATE.EDITED';
const DETAILS_SHOW_KEY = 'CUSTOMER.DETAILS.TAB.USERS.PROFILE_USER_DETAILS.SHOW_DETAILS';
const DETAILS_HIDE_KEY = 'CUSTOMER.DETAILS.TAB.USERS.PROFILE_USER_DETAILS.HIDE_DETAILS';
const USERS_KEYS = 'CUSTOMER.DETAILS.TAB.USERS.';
// Same rule as the backend: something@domain.tld, no spaces
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

@Component({
    selector: 'tfl-profile-users-detail',
    template: require('./profile.users.template.html'),
    providers: [CustomerProfileUsersService, CountryService],
    styles: [`
        .user-created {
            color: #0b60f1 !important;
        }

        .user-approved {
            color: green !important;
        }

        .user-declined {
            color: red !important;
        }

        .user-edited {
            color: #f57511 !important;
        }
    `]
})
export class ProfileUsersComponent implements OnInit {

    readonly usersTabRoute = CUSTOMER_USERS_TAB_ROUTE;
    profileUsers: ProfileUser[] = [];
    profileType: number;
    profileUserStates: [ProfileUserState];
    filterModel: ProfileUsersFilteringModel;
    DEFAULT_OPTION = 'undefined';
    // A state change is in progress: blocks the menu action until the server answers
    isBlocked = false;
    loadError: string = null;
    passwordCopied = false;

    // Manual "create authorised user" dialog state
    showManualDialog = false;
    manualSubmitting = false;
    currentDate: Date = new Date();
    countries: Country[] = [];
    manualCredentials: { email: string, password: string, userId: number } = null;
    manualUser: {
        email: string,
        firstName: string,
        lastName: string,
        middleName: string,
        dateOfBirth: string,
        phone: string,
        passportId: string,
        nationalityId: number,
        passportIssueDate: string,
        passportExpiryDate: string,
        issueCountryId: number,
        issuePlace: string
    } = {
        email: '', firstName: '', lastName: '', middleName: '', dateOfBirth: '', phone: '',
        passportId: '', nationalityId: null, passportIssueDate: '', passportExpiryDate: '',
        issueCountryId: null, issuePlace: ''
    };

    private _profileId: number;

    @InjectOptionDialog('new-user-dialog') dialog: OptionDialog;

    constructor(private _navigation: NavigationService,
                private _customerProfileUsersService: CustomerProfileUsersService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private _notificationManager: NotificationManager,
                private _translateManager: TranslateManager,
                private _countryService: CountryService) {
        this.resetFilterModel()
    }

    ngOnInit(): void {
        this.activatedRoute.parent.params.subscribe(params => {
            this._profileId = +params['id'];
            this.searchProfileUsers();
        });
        this.fetchProfileUserStates();
    }

    onChangeFilter(obj: object): void {
        this.searchProfileUsers();
    }

    searchProfileUsers() {
        this.preventOfDefaultSelectTagOptionOrValues();
        this.loadError = null;
        this._customerProfileUsersService.getAllProfileUsers(this._profileId, this.filterModel)
            .subscribe(
                response => {
                    this.profileType = response.profileType;
                    this.profileUsers = response.profileUsers || [];
                },
                error => this.loadError = this.errorMessage(error)
            );
    }

    private getActionProfileUserDetailText(showDetails: boolean) {
        if (showDetails) {
            return DETAILS_HIDE_KEY;
        } else {
            return DETAILS_SHOW_KEY;
        }
    }


    newUser(): void {
        let addUser = (email?: string) => {
            email = (email || '').trim();
            if (!EMAIL_PATTERN.test(email)) {
                this._notificationManager.error(this._translateManager.getValue(USERS_KEYS + 'INVITE_EMAIL_INVALID'));
                return;
            }
            return this._customerProfileUsersService.addUser(email, this._profileId)
                .subscribe(
                    () => {
                        this._notificationManager.success(this._translateManager.getValue(USERS_KEYS + 'INVITE_MESSAGE'), 5000);
                        this.searchProfileUsers()
                    },
                    error => this._notificationManager.error(this.errorMessage(error))
                );
        };

        let options = {
            title: 'CUSTOMER.DETAILS.TAB.USERS.NEW.DIALOG.TITLE',
            descriptionLines: [{key: USERS_KEYS + 'NEW.DIALOG.MESSAGE'}],
            confirmButtonText: 'CUSTOMER.DETAILS.TAB.USERS.NEW.DIALOG.BUTTONS.INVITE',
            cancelButtonText: 'CUSTOMER.DETAILS.TAB.USERS.NEW.DIALOG.BUTTONS.CANCEL',
            confirmButtonClass: 'btn-primary'
        };
        this.dialog
            .showInputDialog(options, {minlength: 1, maxlength: 150})
            .onConfirmAction((email: string) => addUser(email));
    }

    openManualUserDialog(): void {
        this.manualCredentials = null;
        this.manualSubmitting = false;
        this.passwordCopied = false;
        this.manualUser = {
            email: '', firstName: '', lastName: '', middleName: '', dateOfBirth: '', phone: '',
            passportId: '', nationalityId: null, passportIssueDate: '', passportExpiryDate: '',
            issueCountryId: null, issuePlace: ''
        };
        if (!this.countries || this.countries.length === 0) {
            this._countryService.getCountries()
                .subscribe(countries => this.countries = countries || []);
        }
        this.showManualDialog = true;
    }

    closeManualUserDialog(): void {
        this.showManualDialog = false;
    }

    submitManualUser(): void {
        this.manualSubmitting = true;
        this._customerProfileUsersService.createAuthorisedUserManually(this._profileId, this.manualUser)
            .subscribe(
                (credentials) => {
                    this.manualSubmitting = false;
                    this.manualCredentials = credentials;
                    this._notificationManager.success(this._translateManager.getValue(USERS_KEYS + 'MANUAL.CREATED'), 5000);
                    this.searchProfileUsers();
                },
                error => {
                    this.manualSubmitting = false;
                    this._notificationManager.error(
                        (error && error.message) || this._translateManager.getValue(USERS_KEYS + 'MANUAL.CREATE_ERROR'));
                }
            );
    }

    public getNextUserStateText(userState: string): string {
        switch (userState) {
            case 'Created':
                return STATE_DECLINED_KEY;
            case 'Approved':
                return STATE_DECLINED_KEY;
            case 'Declined':
                return STATE_APPROVED_KEY;
            case 'Edited':
                return STATE_DECLINED_KEY;
        }
    }

    public changeState(profileUser: ProfileUser) {
        if (this.isBlocked) {
            return;
        }
        const nextState = this._translateManager.getValue(this.getNextUserStateText(profileUser.state));
        this.dialog
            .showConfirmDialog({
                title: USERS_KEYS + 'STATE_DIALOG.TITLE',
                descriptionLines: [{
                    key: USERS_KEYS + 'STATE_DIALOG.MESSAGE',
                    params: {user: profileUser.userName, state: nextState}
                }],
                confirmButtonText: USERS_KEYS + 'CONFIRM',
                cancelButtonText: USERS_KEYS + 'CANCEL',
                confirmButtonClass: 'btn-primary'
            })
            .onConfirmAction(() => {
                this.isBlocked = true;
                this._customerProfileUsersService.changeProfileUserState(profileUser.id, this.getStateIdFromName(profileUser.state))
                    .subscribe(
                        () => {
                            this.isBlocked = false;
                            this._notificationManager.success(
                                this._translateManager.getValue('CUSTOMER.DETAILS.TAB.USERS.STATE.EDITED_MESSAGE'),
                                true
                            );
                            this.searchProfileUsers();
                        },
                        error => {
                            this.isBlocked = false;
                            this._notificationManager.error(this.errorMessage(error));
                        }
                    );
            });
    }

    copyPassword(): void {
        if (!this.manualCredentials) {
            return;
        }
        const field = document.createElement('textarea');
        field.value = this.manualCredentials.password;
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        try {
            this.passwordCopied = document.execCommand('copy');
        } finally {
            document.body.removeChild(field);
        }
    }

    private errorMessage(error: any): string {
        return (error && error.message) || this._translateManager.getValue(USERS_KEYS + 'ACTION_ERROR');
    }

    private getStateIdFromName(userState: string): string {
        switch (userState) {
            case 'Created':
                return '3';
            case 'Approved':
                return '3';
            case 'Declined':
                return '2';
            case 'Edited':
                return '3';
        }
    }

    private fetchProfileUserStates() {
        this._customerProfileUsersService.getProfileStatesForRegisteredClients()
            .subscribe(states => (this.profileUserStates = states));
    }

    private preventOfDefaultSelectTagOptionOrValues() {
        this.filterModel = Object.assign(this.filterModel,
            mapValues(this.filterModel, (v) => (String(v) === this.DEFAULT_OPTION || String(v) === '') ? undefined : v));
    }

    private resetFilterModel() {
        this.filterModel = Object.assign(this.filterModel || {}, {
            userName: undefined,
            userStateId: undefined
        });
    }

    private currentUserStateClass(userState: string): string {
        switch (userState) {
            case 'Created':
                return 'user-created';
            case 'Approved':
                return 'user-approved';
            case 'Declined':
                return 'user-declined';
            case 'Edited':
                return 'user-edited';
        }
    }

    private nextUserStateClass(userState: string): string {
        switch (userState) {
            case 'Created':
                return 'user-declined';
            case 'Approved':
                return 'user-declined';
            case 'Declined':
                return 'user-approved';
            case 'Edited':
                return 'user-declined';
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/modules/customers/shared/components/users/profile.users.component.ts